import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  avatar: string;
  content: string;
  university: string;
  rating: number;
}

const Testimonials: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Aashima Hamal",
      role: "Student",
      avatar: "../../../images/students/aashima.jpg",
      content: "Studying in Japan has transformed my academic career and personal life. The guidance I received throughout the application process was invaluable, and the language preparation gave me the confidence to excel in my exams.",
      university: "Nihon Wellness Sports University",
      rating: 5
    },
    {
      id: 2,
      name: "Dilip Thapa Magar",
      role: "Student",
      avatar: "../../../images/students/dilip.jpg",
      content: "From visa application to cultural integration, I received support at every step. The network of alumni helped me find internships and job opportunities. I would highly recommend this journey to anyone planning to study in Japan.",
      university: "Kankyu Academy",
      rating: 5
    },
    {
      id: 3,
      name: "Rajbin Khatri",
      role: "Student",
      avatar: "../../../images/students/rajbin.jpg",
      content: "The guidance was game-changing. The team's knowledge of Japanese universities and admission processes is exceptional and made all the difference.",
      university: "Nagoya AIUEO",
      rating: 4
    },
    {
      id: 4,
      name: "Kajal Khatri",
      role: "Student",
      avatar: "../../../images/students/kajal.jpg",
      content: "The pre-departure orientation program prepared me for life in Japan. The ongoing support even after enrollment helped me navigate academic and personal challenges with ease. My experience studying in Japan has been worth every effort!",
      university: "Nagoya AIUEO",
      rating: 5
    },
    // {
    //   id: 5,
    //   name: "Chen Wei",
    //   role: "Robotics Engineering Ph.D",
    //   avatar: "https://randomuser.me/api/portraits/men/66.jpg",
    //   content: "The research opportunities in Japan are unparalleled. The application assistance I received helped me secure a position at a top laboratory at Tohoku University. Now I'm collaborating with industry leaders and building my global network.",
    //   university: "Tohoku University",
    //   rating: 5
    // }
  ];

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const autoScrollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll functionality
  useEffect(() => {
    if (isAutoScrolling) {
      autoScrollTimerRef.current = setTimeout(() => {
        setDirection(1);
        setCurrent((prevCurrent) => (prevCurrent + 1) % testimonials.length);
      }, 8000); // 8 seconds per testimonial
    }

    return () => {
      if (autoScrollTimerRef.current) {
        clearTimeout(autoScrollTimerRef.current);
      }
    };
  }, [current, testimonials.length, isAutoScrolling]);

  const handleNext = () => {
    if (autoScrollTimerRef.current) {
      clearTimeout(autoScrollTimerRef.current);
    }
    setIsAutoScrolling(false);
    setDirection(1);
    setCurrent((prevCurrent) => (prevCurrent + 1) % testimonials.length);
  };

  const handlePrevious = () => {
    if (autoScrollTimerRef.current) {
      clearTimeout(autoScrollTimerRef.current);
    }
    setIsAutoScrolling(false);
    setDirection(-1);
    setCurrent((prevCurrent) => (prevCurrent - 1 + testimonials.length) % testimonials.length);
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 500 : -500,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 500 : -500,
      opacity: 0,
      scale: 0.9
    })
  };

  // Cherry blossom petal animation
  const petals = Array.from({ length: 25 }).map((_, i) => {
    const size = Math.random() * 20 + 10;
    const initialX = Math.random() * 100;
    const initialY = -100 - Math.random() * 500;
    return (
      <motion.div
        key={i}
        className="absolute bg-primary/20 z-0"
        style={{ 
          width: size, 
          height: size, 
          borderRadius: '50% 50% 50% 0',
          rotate: `${Math.random() * 360}deg`,
          left: `${initialX}%`, 
          top: `${initialY}px` 
        }}
        animate={{
          y: ['0vh', '100vh'],
          x: [0, Math.sin(i) * 100],
          rotate: [`${Math.random() * 360}deg`, `${Math.random() * 360 + 360}deg`]
        }}
        transition={{
          duration: 10 + Math.random() * 20,
          repeat: Infinity,
          delay: i * 0.3,
          ease: "linear"
        }}
      />
    );
  });

  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Floating petals animation */}
      {petals}
      
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>
      
      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 select-none pointer-events-none">
        <div className="japanese-pattern"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block bg-gradient-to-r from-primary to-secondary p-2 rounded-lg mb-4"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </motion.div>
          
          <motion.span 
            className="text-sm font-medium px-4 py-1.5 rounded-full bg-white/10 text-white"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Success Stories
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/80">
            What Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Students Say</span>
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg">
            Hear from students who transformed their academic careers with our guidance and support.
          </p>
        </motion.div>
        
        <div className="relative max-w-4xl mx-auto px-4">
          <div className="overflow-hidden min-h-[400px] md:min-h-[300px]">
            <AnimatePresence custom={direction} initial={false} mode="wait">
              <motion.div
                key={current}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  mass: 1
                }}
                className="glass-dark rounded-2xl p-8 md:p-10 border border-white/5"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  <motion.div 
                    className="flex-shrink-0 flex flex-col items-center md:items-start"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary/30">
                        <img 
                          src={testimonials[current].avatar} 
                          alt={testimonials[current].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <motion.div 
                        className="absolute -bottom-2 -right-2 bg-gradient-to-r from-primary to-secondary text-white text-xs font-bold px-2 py-1 rounded-full flex items-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4, type: "spring" }}
                      >
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        {testimonials[current].rating}.0
                      </motion.div>
                    </div>
                    
                    <motion.div 
                      className="mt-4 text-center md:text-left"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h3 className="font-bold text-xl">{testimonials[current].name}</h3>
                      <p className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary text-sm font-medium mt-1">{testimonials[current].role}</p>
                      <div className="flex items-center justify-center md:justify-start mt-2">
                        <svg 
                          className="w-4 h-4 text-primary mr-1" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                        </svg>
                        <p className="text-white/60 text-xs">{testimonials[current].university}</p>
                      </div>
                    </motion.div>
                  </motion.div>
                  
                  <div className="flex-1">
                    <motion.div 
                      className="relative"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <svg 
                        className="w-12 h-12 text-primary/20 absolute -top-2 -left-1" 
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                      <motion.p 
                        className="text-white/80 leading-relaxed pt-8 text-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        {testimonials[current].content}
                      </motion.p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-center md:justify-between items-center mt-8">
            <motion.button
              onClick={handlePrevious}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/20 text-white hover:bg-white/10 transition-colors"
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              whileTap={{ scale: 0.9 }}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
            
            <div className="hidden md:flex space-x-2 items-center">
              {testimonials.map((_, i) => (
                <motion.button
                  key={i}
                  onClick={() => {
                    setDirection(i > current ? 1 : -1);
                    setCurrent(i);
                    setIsAutoScrolling(false);
                    if (autoScrollTimerRef.current) {
                      clearTimeout(autoScrollTimerRef.current);
                    }
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === current 
                      ? 'bg-gradient-to-r from-primary to-secondary w-10'
                      : 'bg-white/20'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
            
            <motion.button
              onClick={handleNext}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/20 text-white hover:bg-white/10 transition-colors"
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              whileTap={{ scale: 0.9 }}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 